/**
 * Accounts and people beyond Lark — the identifier grammar and its invariants.
 *
 * The `tenants` row stays the account and `users` the person; only the *spelling*
 * of their keys gains namespaces (design 20-identity-design.md §2):
 *
 *   Account (tenants.lark_tenant_key)
 *     lark       <tenant_key>            unchanged, never contains ':'
 *     microsoft  ms:<tid>                Entra tenant GUID, lowercase
 *     google     google:<hd>             Workspace hosted domain, lowercase
 *     email      email:<ulid>            minted; never the address
 *   Person (users.lark_user_id)
 *     lark       <open_id>               unchanged (ou_…)
 *     microsoft  ms:<tid>:<oid>          oid is per tenant and immutable ("use oid + tid")
 *     google     google:<sub>            Google's stable subject
 *     email      email:<ulid>            minted; the address lives in users.email
 *
 * The column names still say "lark"; renaming them would break the Worker that runs
 * during a deploy window and every script. New code calls the value an `accountKey`.
 *
 * Opaque ids for email on purpose: account keys go on every span
 * (`automadoc.tenant_key`), into admin URLs and bot cards, and an address there is
 * personal data in telemetry. A Workspace domain is an organisation's name, kept
 * readable for operators.
 *
 * ## Invariants (each is tested)
 * 1. **A key without ':' is a Lark key** — so every existing
 *    `getTenantAccessToken(…, tenant.lark_tenant_key)` stays correct, and a Lark
 *    tenant_key or open_id containing ':' is refused at login.
 * 2. **One sign-in platform per account** (owner decision 2026-10-17). An account is
 *    Lark, Microsoft, Google or email, fixed when it is created, and its
 *    tenant_identities are all of that platform; no organisation is ever linked into
 *    another platform's account. A session's platform (the JWT's `plat`) is always its
 *    account's (`sessionMatchesAccount`). Personal Google and Microsoft accounts sign in
 *    to an **email** account: they only prove an address, as the emailed code does.
 * 3. **Lark code runs only for Lark sessions** — enforced by the API's middleware
 *    with the session's platform (the JWT's `plat`), not by anything here.
 * 4. **A user belongs to one account**, as today.
 * 5. **Data connections are not membership.** Anyone in any account may connect their
 *    own Microsoft 365 or Google data; that is an OAuth grant read under the
 *    caller-token rule, never a way into another account.
 */

/** Every way a person can sign in. */
export const PLATFORMS = ['lark', 'microsoft', 'google', 'email'] as const
export type Platform = (typeof PLATFORMS)[number]

/** Platforms that are organisations, and so may appear in tenant_identities. */
export type OrgPlatform = 'lark' | 'microsoft' | 'google'

/** OAuth providers whose tokens AutomaDoc keeps (oauth_connections.provider). */
export type OAuthProvider = 'microsoft' | 'google'

/** An account's key (tenants.lark_tenant_key). A plain string; the alias documents intent. */
export type AccountKey = string

/**
 * The Entra "tenant" every personal Microsoft account (outlook.com, live.com) signs in
 * through. Refused as an organisation: it is millions of unrelated people.
 */
export const MICROSOFT_PERSONAL_TENANT_ID = '9188040d-6c67-4c5b-b112-36a304b66dad'

const PREFIX = { microsoft: 'ms', google: 'google', email: 'email' } as const
const PLATFORM_OF_PREFIX: Record<string, Exclude<Platform, 'lark'>> = {
  ms: 'microsoft',
  google: 'google',
  email: 'email',
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
// A hostname: labels of letters, digits and hyphens, at least one dot. Lowercase only —
// callers lowercase first, so two spellings of one domain cannot become two accounts.
const DOMAIN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/
// Crockford base32, 26 characters: what mintUlid() produces.
const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/
// Google's `sub`: up to 255 ASCII characters, in practice 21 digits. No ':' allowed,
// or the key could not be split back.
const GOOGLE_SUB = /^[A-Za-z0-9_-]{1,255}$/

/** The Entra tenant id as it is keyed: lowercase GUID, or null when it is not one. */
export function normalizeMicrosoftTenantId(tid: string): string | null {
  const value = tid.trim().toLowerCase()
  return GUID.test(value) ? value : null
}

/** The Workspace domain as it is keyed: lowercase hostname, or null when it is not one. */
export function normalizeGoogleDomain(hd: string): string | null {
  const value = hd.trim().toLowerCase().replace(/\.$/, '')
  return DOMAIN.test(value) ? value : null
}

/**
 * The account key of a Microsoft or Google organisation. Throws on an id that is not
 * one — an id that reached this far unvalidated is a bug, not a user error.
 */
export function accountKeyFor(platform: 'microsoft' | 'google', platformTenantId: string): AccountKey {
  const id = platform === 'microsoft' ? normalizeMicrosoftTenantId(platformTenantId) : normalizeGoogleDomain(platformTenantId)
  if (!id) throw new Error(`Not a ${platform} organisation id`)
  if (platform === 'microsoft' && id === MICROSOFT_PERSONAL_TENANT_ID) {
    throw new Error('Personal Microsoft accounts are not an organisation')
  }
  return `${PREFIX[platform]}:${id}`
}

/** The account key of an email-only account, from a minted ULID. */
export function emailAccountKey(ulid: string): AccountKey {
  if (!ULID.test(ulid)) throw new Error('Not a ULID')
  return `email:${ulid}`
}

/** A person's id (users.lark_user_id) on a platform that is not Lark. */
export function userIdFor(platform: 'microsoft', tid: string, oid: string): string
export function userIdFor(platform: 'google', sub: string): string
export function userIdFor(platform: 'email', ulid: string): string
export function userIdFor(platform: 'microsoft' | 'google' | 'email', a: string, b?: string): string {
  if (platform === 'microsoft') {
    const tid = normalizeMicrosoftTenantId(a)
    const oid = normalizeMicrosoftTenantId(b ?? '') // an oid is a GUID too
    if (!tid || !oid) throw new Error('Not a Microsoft tid/oid')
    return `ms:${tid}:${oid}`
  }
  if (platform === 'google') {
    if (!GOOGLE_SUB.test(a)) throw new Error('Not a Google subject')
    return `google:${a}`
  }
  if (!ULID.test(a)) throw new Error('Not a ULID')
  return `email:${a}`
}

/** Invariant 1: no ':' means Lark. Empty is nothing at all. */
export function isLarkAccountKey(key: string): boolean {
  return key.length > 0 && !key.includes(':')
}

/**
 * Whether a tenant_key or open_id Lark handed us may be used as a key. Lark's are hex
 * or `ou_…`; one with ':' would read as another platform's key, so it is refused at
 * login (defence in depth: Lark has never issued one).
 */
export function isValidLarkId(value: unknown): value is string {
  return typeof value === 'string' && isLarkAccountKey(value)
}

/**
 * The platform an account key belongs to, or null for a namespaced key this code does
 * not know (malformed, or written by a newer release). Callers deciding "is this Lark"
 * use isLarkAccountKey(), which has no null case.
 */
export function platformOfAccountKey(key: string): Platform | null {
  if (isLarkAccountKey(key)) return 'lark'
  const colon = key.indexOf(':')
  if (colon <= 0) return null
  const platform = PLATFORM_OF_PREFIX[key.slice(0, colon)]
  if (!platform) return null
  const rest = key.slice(colon + 1)
  if (platform === 'microsoft') return GUID.test(rest) ? platform : null
  if (platform === 'google') return DOMAIN.test(rest) ? platform : null
  return ULID.test(rest) ? platform : null
}

/** The platform a person's id belongs to, or null for a malformed namespaced id. */
export function platformOfUserId(id: string): Platform | null {
  if (isLarkAccountKey(id)) return 'lark'
  const [prefix, ...rest] = id.split(':')
  const platform = PLATFORM_OF_PREFIX[prefix]
  if (platform === 'microsoft') return rest.length === 2 && rest.every((p) => GUID.test(p)) ? platform : null
  if (platform === 'google') return rest.length === 1 && GOOGLE_SUB.test(rest[0]) ? platform : null
  if (platform === 'email') return rest.length === 1 && ULID.test(rest[0]) ? platform : null
  return null
}

/** The organisation id inside a namespaced account key (`ms:<tid>` → tid), or null for Lark/email. */
export function orgIdOfAccountKey(key: string): { platform: 'microsoft' | 'google'; platformTenantId: string } | null {
  const platform = platformOfAccountKey(key)
  if (platform !== 'microsoft' && platform !== 'google') return null
  return { platform, platformTenantId: key.slice(key.indexOf(':') + 1) }
}

/**
 * Invariant 2: may a session of `sessionPlatform` act in this account? Only when it is
 * the account's own platform. A key this code does not know matches nothing.
 */
export function sessionMatchesAccount(accountKey: AccountKey, sessionPlatform: Platform): boolean {
  return platformOfAccountKey(accountKey) === sessionPlatform
}

// =============================================================================
// Minted ids
// =============================================================================

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/**
 * A ULID: 48 bits of time, 80 random. Sortable by creation, which keeps an operator's
 * list of email accounts in signup order, and opaque, which keeps the address out of
 * every key.
 */
export function mintUlid(now: number = Date.now(), random: Uint8Array = crypto.getRandomValues(new Uint8Array(10))): string {
  let time = ''
  let t = now
  for (let i = 0; i < 10; i++) {
    time = CROCKFORD[t % 32] + time
    t = Math.floor(t / 32)
  }
  // 80 random bits as 16 base32 characters.
  let bits = 0
  let value = 0
  let rand = ''
  for (const byte of random.slice(0, 10)) {
    value = (value << 8) | byte
    bits += 8
    while (bits >= 5) {
      rand += CROCKFORD[(value >>> (bits - 5)) & 31]
      bits -= 5
    }
  }
  return time + rand
}

// =============================================================================
// Environment switches and trials
// =============================================================================

/**
 * The platforms an environment has switched on, from IDENTITY_PLATFORMS
 * (comma-separated). Lark is always on — it is the product every existing customer
 * uses and its routes predate the switch — so unset, empty or garbage means Lark only.
 */
export function parseIdentityPlatforms(value: string | undefined | null): Platform[] {
  const wanted = new Set(
    (value ?? '')
      .split(',')
      .map((p) => p.trim().toLowerCase())
      .filter((p): p is Platform => (PLATFORMS as readonly string[]).includes(p)),
  )
  wanted.add('lark')
  return PLATFORMS.filter((p) => wanted.has(p))
}

/**
 * Decision §11.3: may this account be given the automatic Pro trial?
 *
 * Organisations — a Lark tenant, an Entra tenant (`tid`), a Workspace domain (`hd`) —
 * keep the trial exactly as Lark tenants have it. Email-only accounts, and personal
 * Google sign-ins (which become email accounts, never a domain), do not: an unlimited
 * trial on a throwaway address is what gets farmed. They keep the Free allowance with
 * the footer. An operator can still issue a manual `trial` grant; this only decides
 * what is offered and self-started.
 */
export function trialEligible(accountKey: AccountKey): boolean {
  const platform = platformOfAccountKey(accountKey)
  return platform === 'lark' || platform === 'microsoft' || platform === 'google'
}

// =============================================================================
// What the company portal shows, by platform (design §8.1)
// =============================================================================

export interface PlatformFeatures {
  /** Home's main card: Lark Bases, or the data sources connected outside Lark. */
  home: 'bases' | 'dataSources'
  templates: {
    /** Templates bound to a Lark Base table. */
    base: boolean
    /** Templates on Excel, SharePoint Lists, Dataverse or Google Sheets: every account may connect its own data. */
    sources: boolean
    /** Templates filled only through the document API. */
    api: boolean
  }
  /** Approvals and approver signatures: Lark only, the server refuses other sessions too. */
  approvals: boolean
  approverSignatures: boolean
  generations: boolean
  /**
   * Signature requests: a Lark session only, until the e-sign module sends from every source
   * (a page there could only say "not available here"). Colleague delivery is by Lark bot.
   */
  signatures: boolean
  signatureColleagues: boolean
  billing: boolean
  pricing: boolean
  apiKeys: boolean
  connections: {
    /** Connect / disconnect data scopes, admin consent, Dataverse environments: every account. */
    dataScopes: boolean
    /** The data providers this environment offers to connect, whatever the sign-in platform. */
    providers: Array<'microsoft' | 'google'>
  }
  /** People: members, roles, join requests and invitations. Every account. */
  members: boolean
  /**
   * "Who can join": an organisation account (Lark tenant, Entra tenant, Workspace domain)
   * chooses between anyone from it and only people an admin approves. An email account
   * has no organisation to check; invitations are its only way in.
   */
  joinPolicy: boolean
  /**
   * Invite by email (with a role and, optionally, a seat): every account, where this
   * environment can send mail (GET /api/auth/providers `emailSending`).
   */
  invitations: boolean
}

/**
 * The portal's screens for a session (design §8.1's table), in one place so the shell
 * and the route guards agree. The server enforces the Lark-only screens on its own;
 * this only decides what is shown.
 *
 * Since 2026-10-17 an account has one sign-in platform, so what is shown follows the
 * session's platform alone; `platforms` is kept for the call sites and no longer read.
 *
 * @param _platforms  ignored (it listed linked identities; an account has only its own)
 * @param session     the platform the person signed in with (always the account's)
 * @param enabled     the environment's platforms (GET /api/auth/providers); defaults to all
 * @param options     `emailSending`: whether this environment can send mail (GET
 *                    /api/auth/providers `emailSending`); invitations need it. Unknown counts as yes.
 */
export function platformFeatures(
  _platforms: readonly Platform[],
  session: Platform,
  enabled: readonly Platform[] = PLATFORMS,
  options: { emailSending?: boolean } = {},
): PlatformFeatures {
  const lark = session === 'lark'
  const providers = (['microsoft', 'google'] as const).filter((p) => enabled.includes(p))
  return {
    home: lark ? 'bases' : 'dataSources',
    templates: {
      base: lark,
      // A Lark account's source templates read data its people connect, so they need a
      // provider switched on; the other platforms' sign-in is itself one.
      sources: lark ? providers.length > 0 : true,
      api: !lark,
    },
    approvals: lark,
    approverSignatures: lark,
    generations: true,
    // Sending for signature starts from a Lark Base template today; until the e-sign
    // module opens it to every source, the screen is not shown where it cannot be used.
    signatures: lark,
    signatureColleagues: lark,
    billing: true,
    pricing: true,
    apiKeys: true,
    connections: {
      dataScopes: providers.length > 0,
      providers,
    },
    members: true,
    joinPolicy: session !== 'email',
    invitations: options.emailSending ?? true,
  }
}

// =============================================================================
// API shapes (identity routes)
// =============================================================================

/** GET /api/auth/providers */
export interface AuthProvidersResponse {
  /** Switched on in this environment; Lark always. */
  platforms: Platform[]
  /** Public client ids the portal needs to build authorize URLs, for enabled providers only. */
  clients: {
    microsoft?: { clientId: string }
    google?: { clientId: string }
    /** Email sign-in: the Turnstile widget's site key (action `email_start`), for /api/auth/email/start. */
    email?: { turnstileSiteKey: string }
  }
  /** Whether this environment can send mail (invitations, email sign-in): platformFeatures' `emailSending`. */
  emailSending?: boolean
  /**
   * AutomaDoc Sign is on here (SIGN_AVAILABLE, esign/model.ts isSignAvailable). Absent (an
   * older API) or false: the portal shows the legacy Signatures page, never #/esign.
   */
  sign?: boolean
}

export type MemberRole = 'admin' | 'member'

export interface AccountMemberSummary {
  userId: string
  name: string | null
  email: string | null
  platform: Platform
  role: MemberRole
  /** Always 'active' here: join requests are listed by GET /api/tenant/join-requests. */
  status: 'active'
  grantedBy: string
  grantedAt: number
}

/** GET /api/tenant/members */
export interface ListMembersResponse {
  members: AccountMemberSummary[]
}

/** PUT /api/tenant/members/:userId */
export interface UpdateMemberRequest {
  role: MemberRole
}

// =============================================================================
// Who can join (owner decision 2026-10-17, membership)
// =============================================================================

/**
 * How people of the account's own organisation get in:
 * - `open`: anyone signing in from it becomes a member (the default, as before).
 * - `approval`: they are recorded as a join request and get no session until an admin
 *   approves. Platform admins (Lark admins, Entra admin roles or AutomaDoc.Admin, a
 *   Workspace super admin) always get in, and an accepted invitation is an approval.
 * Email accounts have no organisation: invitations are their only way in, whatever this says.
 */
export const JOIN_POLICIES = ['open', 'approval'] as const
export type JoinPolicy = (typeof JOIN_POLICIES)[number]

export function isJoinPolicy(value: unknown): value is JoinPolicy {
  return typeof value === 'string' && (JOIN_POLICIES as readonly string[]).includes(value)
}

/** Whether an account's join policy means anything: it has an organisation to check against. */
export function joinPolicyApplies(accountKey: AccountKey): boolean {
  const platform = platformOfAccountKey(accountKey)
  return platform === 'lark' || platform === 'microsoft' || platform === 'google'
}

/** A person's standing in an account, as the People list shows it. */
export type MemberStanding = 'active' | 'requested' | 'invited' | 'removed'

/** GET /api/tenant/join-policy (and what PUT answers with). */
export interface JoinPolicyResponse {
  policy: JoinPolicy
  /** False for an email account: there is no organisation, so only invitations bring people in. */
  applies: boolean
  /** The organisation people must sign in from: its platform, id (tenant key, tid, domain) and display name. */
  organisation: { platform: OrgPlatform; id: string; name: string | null } | null
}

/** PUT /api/tenant/join-policy */
export interface UpdateJoinPolicyRequest {
  policy: JoinPolicy
}

/** One person waiting for approval (GET /api/tenant/join-requests). */
export interface JoinRequestSummary {
  userId: string
  name: string | null
  /** The address they signed in with, when the platform gave one. */
  email: string | null
  platform: Platform
  status: 'requested'
  /** When they first asked. */
  requestedAt: number
}

export interface ListJoinRequestsResponse {
  requests: JoinRequestSummary[]
}

/** POST /api/tenant/join-requests/:userId/approve */
export interface ApproveJoinRequestRequest {
  /** Defaults to member. */
  role?: MemberRole
}

/** POST /api/tenant/join-requests/:userId/{approve,decline} */
export interface JoinRequestDecisionResponse {
  success: true
  userId: string
  status: 'active' | 'removed'
  role?: MemberRole
}

/**
 * 403 JOIN_REQUESTED, from any sign-in route when the account's join policy is
 * 'approval' and the person is not yet approved. No session or token comes with it.
 */
export interface JoinRequestedBody {
  error: string
  code: 'JOIN_REQUESTED'
  accountName: string | null
  /** When the request was first recorded. */
  requestedAt: number
}

// =============================================================================
// Personal sign-ins: which addresses a provider vouches for (G4 review O1)
// =============================================================================

/**
 * The mail domains a consumer provider runs itself. Both providers let anyone make a
 * personal account named after an address *elsewhere* (a Google account or a Microsoft
 * account "with your own email"): the address was proven once, when the account was
 * made, and the account outlives the mailbox — a former employee keeps a personal
 * Google account called ops@acme.com long after acme.com closed their mailbox. Only for
 * an address on the provider's own mail service is the provider still the mailbox's
 * owner, so only there does its sign-in prove the mailbox the way our emailed code does.
 *
 * Deliberately an explicit list, not a pattern (`live.*` would take in domains others
 * own). A Microsoft or Google consumer domain missing here is not refused: the person
 * proves the address once by emailed code and the sign-in is then bound to them
 * (utils/accounts.ts `resolvePersonalPerson`).
 */
export const PROVIDER_MAIL_DOMAINS: Readonly<Record<'google' | 'microsoft', readonly string[]>> = {
  google: ['gmail.com', 'googlemail.com'],
  microsoft: [
    'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'passport.com',
    'outlook.fr', 'outlook.de', 'outlook.es', 'outlook.it', 'outlook.jp', 'outlook.kr', 'outlook.sa',
    'outlook.co.id', 'outlook.co.th', 'outlook.com.vn', 'outlook.com.au', 'outlook.com.br', 'outlook.in',
    'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.es', 'hotmail.it', 'hotmail.co.jp', 'hotmail.com.br',
    'live.co.uk', 'live.fr', 'live.de', 'live.it', 'live.jp', 'live.com.au', 'live.ca', 'live.nl',
  ],
}

/**
 * Whether a personal (consumer) account of this provider proves the mailbox of `email`:
 * the address is on the provider's own mail service. Anything else needs the emailed
 * code once (design §13 rule 3, as amended by the G4 review).
 */
export function providerOwnsAddress(provider: 'google' | 'microsoft', email: string): boolean {
  const at = email.lastIndexOf('@')
  if (at < 1) return false
  const domain = email.slice(at + 1).trim().toLowerCase()
  return PROVIDER_MAIL_DOMAINS[provider].includes(domain)
}
